import { useState } from 'react';
import { CheckCircle2, FileCheck, X } from 'lucide-react';
import { SignOff, User } from '../types';
import { signOffSequence } from '../services/signoffs';
import './SignOffModal.css';

interface SignOffModalProps {
  appId: string;
  sequenceId: string;
  user: User;
  signoffs: SignOff[]; // all sign-offs for the application (live)
  onClose: () => void;
}

// Electronic review sign-off for the currently selected sequence.
// The signature is manifested by re-entering the login password; the record
// is immutable and every sign-off is written to the audit trail.
export function SignOffModal({ appId, sequenceId, user, signoffs, onClose }: SignOffModalProps) {
  const [meaning, setMeaning] = useState<SignOff['meaning']>('review_completed');
  const [comment, setComment] = useState('');
  const [password, setPassword] = useState('');
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const sequenceSignoffs = signoffs.filter((s) => s.sequenceId === sequenceId);
  const alreadySigned = sequenceSignoffs.some((s) => s.userUid === user.uid);

  const meaningLabel = (m: SignOff['meaning']) =>
    m === 'approved' ? 'Approved' : m === 'in_review' ? 'In review' : 'Review completed';

  const handleSign = async () => {
    if (!password || signing) return;
    setSigning(true);
    setError(null);
    try {
      await signOffSequence({
        appId,
        sequenceId,
        meaning,
        comment: comment.trim() || undefined,
        user: { uid: user.uid, email: user.email },
        password,
      });
      setSuccess(true);
      setPassword('');
      setComment('');
    } catch (err: any) {
      console.error('Sign-off failed:', err);
      const msg = /wrong-password|invalid-credential|invalid-login/i.test(err?.code || err?.message || '')
        ? 'Incorrect password - sign-off not recorded.'
        : `Sign-off failed: ${err?.message || err}`;
      setError(msg);
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="signoff-overlay" onClick={onClose}>
      <div className="signoff-modal" onClick={(e) => e.stopPropagation()}>
        <div className="signoff-header">
          <h2>
            <FileCheck className="icon" />
            Review Sign-off
          </h2>
          <button className="signoff-close" onClick={onClose} title="Close">
            <X className="icon" />
          </button>
        </div>

        <div className="signoff-context">
          <div>
            <span className="signoff-context-label">Application</span>
            <span className="signoff-context-value">{appId}</span>
          </div>
          <div>
            <span className="signoff-context-label">Sequence</span>
            <span className="signoff-context-value">{sequenceId}</span>
          </div>
        </div>

        <div className="signoff-existing">
          <h3>Sign-offs on this sequence ({sequenceSignoffs.length})</h3>
          {sequenceSignoffs.length === 0 ? (
            <p className="signoff-none">No sign-offs recorded yet.</p>
          ) : (
            <ul>
              {sequenceSignoffs.map((s) => (
                <li key={s.id}>
                  <CheckCircle2 className="icon signed" />
                  <span className="signoff-who">{s.userEmail}</span>
                  <span className={`signoff-meaning ${s.meaning}`}>{meaningLabel(s.meaning)}</span>
                  <span className="signoff-when">{s.signedAt.toLocaleString()}</span>
                  {s.comment && <span className="signoff-comment">"{s.comment}"</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        {success ? (
          <div className="signoff-success">
            <CheckCircle2 className="icon" />
            Sign-off recorded. This action has been added to the audit trail.
          </div>
        ) : (
          <div className="signoff-form">
            <h3>{alreadySigned ? 'Add another sign-off' : 'Sign off this sequence'}</h3>
            <div className="signoff-field">
              <label>Meaning of signature</label>
              <select value={meaning} onChange={(e) => setMeaning(e.target.value as SignOff['meaning'])}>
                <option value="in_review">In review</option>
                <option value="review_completed">Review completed</option>
                <option value="approved">Approved</option>
              </select>
            </div>
            <div className="signoff-field">
              <label>Comment (optional)</label>
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="e.g. No open findings"
                maxLength={200}
              />
            </div>
            <div className="signoff-field">
              <label>Confirm your identity - enter your password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSign();
                }}
                placeholder="Your login password"
                autoComplete="new-password"
                name="signoff-password"
              />
            </div>
            <p className="signoff-notice">
              Signing as <strong>{user.email}</strong>. Your electronic signature will be recorded with a secure
              timestamp and cannot be modified or deleted.
            </p>
            {error && <div className="signoff-error">{error}</div>}
            <div className="signoff-actions">
              <button className="signoff-sign-button" onClick={handleSign} disabled={!password || signing}>
                {signing ? 'Signing…' : 'Sign electronically'}
              </button>
              <button className="signoff-cancel-button" onClick={onClose} disabled={signing}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
